import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Loader, LoadingOverlay, SimpleGrid, Table, Text } from '@mantine/core';
import { DriveInfoPanelsEnum } from '@/types/enums/modules';
import { TableHeadList } from '@/containers/pages/filials/edit/components/tabs/disk-space/components/table/head';
import { TableRowList } from '@/containers/pages/filials/edit/components/tabs/disk-space/components/table/row';
import { useAppSelector } from '@hooks/redux/redux';
import { useGetQuotasQuery } from '@/entities/admin-app/quotas/api';
import { IFilialsInfoCommon } from '@/entities/admin-app/filials';
import { useActions } from '@hooks/redux/action';
import { IFiltersEnumType } from '@/types/enums';
import { IQuota } from '@/entities/admin-app/quotas';
import { useGetColumns } from '@/containers/pages/filials/edit/components/tabs/disk-space/hooks/useGetColumns';
import InfiniteScroll from 'react-infinite-scroll-component';
import { IconPlus } from '@tabler/icons';
import { themeColors } from '@/theme/colors';
import { UsersQuotaModal } from '@/containers/pages/filials/edit/components/tabs/disk-space/components/quota-modal';
import { useTranslation } from 'react-i18next';
import { useStyles } from '../../../../../styles';
import { Modal } from '@components/modal';
import { AssignQuotaModal } from '../assign-quota-modal';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/containers/routes/constants';

interface IFilialQuotaProps {
  filial: IFilialsInfoCommon;
}
export const UsersQuotaList = ({ filial }: IFilialQuotaProps) => {
  const { t } = useTranslation();
  const { classes, cx } = useStyles();
  const scrollRef = useRef<any>(null);
  const [isModalOpened, setIsModalOpened] = useState(false);
  const [isAssignModalOpened, setIsAssignModalOpened] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { setQuotas, setQuotasFilters, setSelectedQuota } = useActions();
  const { quotas, totalQuotas, quotasFilters, selectedQuota } = useAppSelector(
    (state) => state.quotas
  );
  const navigate = useNavigate();
  const { data, /*error,*/ isLoading } = useGetQuotasQuery({
    Count: quotasFilters.Count,
    Offset: quotasFilters.Offset,
    LoadUseCount: true,
    FilterConditions: filial
      ? [
          {
            Type: IFiltersEnumType.FilialFilter,
            NumValues: [filial?.Id]
          }
        ]
      : []
  });

  const onEdit = (quota: IQuota) => {
    setSelectedQuota(quota);
    setIsModalOpened(true);
  };
  const { columns } = useGetColumns({ onEdit: onEdit });

  useEffect(() => {
    if (!data) {
      return;
    }
    const quotasData = quotasFilters.Offset > 0 ? [...quotas, ...data.Quotas] : data.Quotas;
    setQuotas({ ...data, Quotas: quotasData });
  }, [data]);

  const handleLoadMore = () => {
    setQuotasFilters({
      ...quotasFilters,
      Offset: quotasFilters.Offset + quotasFilters.Count
    });
  };

  const resetQuotaFilters = () => {
    scrollRef.current.el.scrollTo(0, 0);
    setQuotasFilters({
      Count: 10,
      Offset: 0
    });
  };

  useEffect(() => {
    if (quotasFilters.Offset === 0) {
      scrollRef.current.el.scrollTo(0, 0);
    }
  }, [quotasFilters]);

  const handleScroll = () => {
    setScrolled(true);
  };

  return (
    <Box className={classes.section} data-section={DriveInfoPanelsEnum.quotas}>
      <Text className={classes.title}>{t('filials.diskSpace.quotas')}</Text>
      <InfiniteScroll
        ref={scrollRef}
        dataLength={quotas?.length}
        next={handleLoadMore}
        hasMore={totalQuotas > quotas?.length}
        loader={
          quotas?.length ? (
            <Box className={classes.infiniteScrollMoreData}>
              <Loader size="sm" />
            </Box>
          ) : null
        }
        className={classes.infiniteScroll}
        height={quotas?.length < 5 ? 'auto' : '400px'}
        onScroll={handleScroll}
      >
        {quotas && quotas?.length > 0 ? (
          <Table horizontalSpacing="md" verticalSpacing="xs" mt="sm">
            <thead className={cx(classes.tableHeader, { [classes.scrolled]: scrolled })}>
              <TableHeadList columns={columns} />
            </thead>
            <tbody>
              <TableRowList rows={quotas} columns={columns} />
            </tbody>
          </Table>
        ) : null}
      </InfiniteScroll>
      <SimpleGrid cols={{ base: 1, sm: 2 }} w="100%">
        <Button
          className={classes.addButton}
          size="sm"
          mt="sm"
          variant="outline"
          rightSection={<IconPlus color={themeColors.brandGray[0]} />}
          onClick={() => {
            setIsModalOpened(true);
          }}
        >
          {t('filials.diskSpace.quotaCreate')}
        </Button>
      </SimpleGrid>
      <SimpleGrid cols={{ base: 1, sm: 2 }} w="100%">
        <Button
          className={classes.addButton}
          size="sm"
          mt="sm"
          variant="outline"
          rightSection={<IconPlus color={themeColors.brandGray[0]} />}
          onClick={() => {
            setIsAssignModalOpened(true);
          }}
        >
          {t('filials.diskSpace.addQuotaAppointment')}
        </Button>
      </SimpleGrid>

      <SimpleGrid my="22px">
        <Text fz="sm" c="#667085">
          {t('filials.diskSpace.quotasDescription')}
        </Text>
      </SimpleGrid>
      <Button
        size="sm"
        px="lg"
        onClick={() => navigate(ROUTES.filials.edit.pages.subRoutes.quotas.path)}
      >
        {t('filials.diskSpace.quotasTable')}
      </Button>
      <LoadingOverlay visible={isLoading} />
      <AssignQuotaModal
        opened={isAssignModalOpened}
        handleClose={() => setIsAssignModalOpened(false)}
      />
      <Modal
        size="450px"
        opened={isModalOpened}
        title={t(`filials.diskSpace.${selectedQuota ? 'editUsersQuota' : 'createUsersQuota'}`)}
        onClose={() => {
          setSelectedQuota(null);
          setIsModalOpened(false);
          resetQuotaFilters();
        }}
      >
        <UsersQuotaModal
          onClose={() => {
            setSelectedQuota(null);
            setIsModalOpened(false);
            resetQuotaFilters();
          }}
          quota={selectedQuota}
          CustomerId={filial?.Id}
        />
      </Modal>
    </Box>
  );
};
